import axios from 'axios'
import { clearWeekReportStatsStorage } from '@/utils/weekReportStats'
import { clearAccessToken } from '@/utils/accessToken'

/** 服务端注销（作废 Refresh、清 HttpOnly Cookie），与 refresh 同属 user 模块 */
export const AUTH_LOGOUT_PATH = '/api/app/core/user/logout'

/**
 * 登录态与用户信息均使用 localStorage，关闭浏览器 / 嵌入 App WebView 后仍可持久化（同源策略下与原生缓存策略一致）。
 * 不再使用 sessionStorage 保存 loggedIn，避免标签关闭即丢失。
 */
const KEY_LOGGED_IN = 'loggedIn'
const KEY_REMEMBER_ME = 'rememberMe'
const KEY_SAVED_USERNAME = 'savedUsername'
const KEY_USER_ID = 'userId'
const KEY_USER_NAME = 'userName'
const KEY_GENDER = 'gender'
const KEY_AVATAR = 'avatar'
/** 我的页默认头像：man / woman，仅存本地 */
const KEY_AVATAR_PRESET = 'profileAvatarPreset'
const KEY_PROFILE_PHONE = 'profilePhone'
const KEY_PROFILE_EMAIL = 'profileEmail'
const KEY_ACCESS_TOKEN_EXPIRE = 'accessTokenExpireTime'
const KEY_REFRESH_TOKEN_EXPIRE = 'refreshTokenExpireTime'

function ignoreStorageError(e) {
  void e
}

/** 是否已登录（读 localStorage；若存在旧版 sessionStorage 标记则迁移一次） */
export function isLoggedInStored() {
  try {
    if (localStorage.getItem(KEY_LOGGED_IN) === '1') return true
    const legacy = sessionStorage.getItem(KEY_LOGGED_IN)
    if (legacy === '1') {
      localStorage.setItem(KEY_LOGGED_IN, '1')
      sessionStorage.removeItem(KEY_LOGGED_IN)
      return true
    }
  } catch (e) {
    ignoreStorageError(e)
  }
  return false
}

/** 用户是否勾选了「记住我」 */
export function isRememberMeEnabled() {
  try {
    return localStorage.getItem(KEY_REMEMBER_ME) === '1'
  } catch (e) {
    ignoreStorageError(e)
    return false
  }
}

export function saveRememberMePreference(enabled) {
  try {
    if (enabled) {
      localStorage.setItem(KEY_REMEMBER_ME, '1')
    } else {
      localStorage.removeItem(KEY_REMEMBER_ME)
    }
  } catch (e) {
    ignoreStorageError(e)
  }
}

export function getSavedUsername() {
  try {
    return localStorage.getItem(KEY_SAVED_USERNAME) || ''
  } catch (e) {
    ignoreStorageError(e)
    return ''
  }
}

export function saveSavedUsername(username) {
  try {
    const name = username != null ? String(username).trim() : ''
    if (name) {
      localStorage.setItem(KEY_SAVED_USERNAME, name)
    } else {
      localStorage.removeItem(KEY_SAVED_USERNAME)
    }
  } catch (e) {
    ignoreStorageError(e)
  }
}

/** 冷启动时是否应尝试用 Refresh Cookie 恢复会话 */
export function shouldAttemptSessionRestore() {
  if (isRememberMeEnabled()) return true
  if (isLoggedInStored()) return true
  try {
    const refreshExp = localStorage.getItem(KEY_REFRESH_TOKEN_EXPIRE)
    if (refreshExp && Date.now() < Date.parse(String(refreshExp).trim())) {
      return true
    }
  } catch (e) {
    ignoreStorageError(e)
  }
  return false
}

export function setLoggedInFlag() {
  try {
    localStorage.setItem(KEY_LOGGED_IN, '1')
    sessionStorage.removeItem(KEY_LOGGED_IN)
  } catch (e) {
    ignoreStorageError(e)
  }
}

export function clearLoggedInFlag() {
  try {
    localStorage.removeItem(KEY_LOGGED_IN)
    sessionStorage.removeItem(KEY_LOGGED_IN)
  } catch (e) {
    ignoreStorageError(e)
  }
}

export function clearRememberMePreference() {
  try {
    localStorage.removeItem(KEY_REMEMBER_ME)
  } catch (e) {
    ignoreStorageError(e)
  }
}

function clearAxiosDefaultAuthHeader() {
  try {
    if (axios.defaults.headers) {
      if (axios.defaults.headers.common) {
        delete axios.defaults.headers.common.Authorization
      }
      delete axios.defaults.headers.Authorization
    }
  } catch (e) {
    ignoreStorageError(e)
  }
}

function clearNonHttpOnlyCookies() {
  try {
    if (typeof document === 'undefined') return
    const raw = document.cookie || ''
    if (!raw) return
    const cookies = raw.split(';')
    cookies.forEach((item) => {
      const name = item.split('=')[0] && item.split('=')[0].trim()
      if (!name) return
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    })
  } catch (e) {
    ignoreStorageError(e)
  }
}

/** 与登录态相关的 sessionStorage（非密钥，退出时一并清掉避免串号） */
function clearAuthRelatedSessionStorage() {
  try {
    sessionStorage.removeItem('summit-detail-payload')
    sessionStorage.removeItem('summary-list-scroll-top')
  } catch (e) {
    ignoreStorageError(e)
  }
}

/**
 * 清除前端所有与 Token / 登录态相关的状态（内存 Access、过期时间、本地资料、周报缓存等）。
 * 401、路由失败等场景只调此函数即可。
 */
export function clearAuthState() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:logout'))
  }
  clearAccessToken()
  clearAxiosDefaultAuthHeader()
  clearNonHttpOnlyCookies()
  clearLoggedInFlag()
  clearRememberMePreference()
  clearUserProfile()
  clearTokenExpireMeta()
  clearWeekReportStatsStorage()
  clearAuthRelatedSessionStorage()
}

/**
 * 主动退出：先请求服务端注销（携带 Cookie + 内存 Access，便于服务端作废 Refresh / 清 HttpOnly），再清本地。
 * 服务端未实现或网络失败时仍会执行 clearAuthState，确保本地无残留 Token。
 */
export async function logoutWithServer() {
  try {
    await axios.post(AUTH_LOGOUT_PATH, {})
  } catch (e) {
    ignoreStorageError(e)
  }
  clearAuthState()
}

/**
 * 保存 Access / Refresh 过期时间（后端 JsonFormat 字符串或时间戳；仅作展示或前端预判，非密钥）
 * @param {{ accessTokenExpireTime?: string|number|Date, refreshTokenExpireTime?: string|number|Date }} payload
 */
export function saveTokenExpireMeta(payload) {
  if (!payload || typeof payload !== 'object') return
  try {
    const a = payload.accessTokenExpireTime
    const r = payload.refreshTokenExpireTime
    if (a != null && String(a).trim() !== '') {
      localStorage.setItem(KEY_ACCESS_TOKEN_EXPIRE, normalizeExpireForStorage(a))
    }
    if (r != null && String(r).trim() !== '') {
      localStorage.setItem(KEY_REFRESH_TOKEN_EXPIRE, normalizeExpireForStorage(r))
    }
  } catch (e) {
    ignoreStorageError(e)
  }
}

function normalizeExpireForStorage(v) {
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return v.toISOString()
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return new Date(v).toISOString()
  }
  return String(v).trim()
}

/**
 * @returns {{ accessTokenExpireTime: string, refreshTokenExpireTime: string }}
 */
export function getStoredTokenExpireMeta() {
  return {
    accessTokenExpireTime: localStorage.getItem(KEY_ACCESS_TOKEN_EXPIRE) || '',
    refreshTokenExpireTime: localStorage.getItem(KEY_REFRESH_TOKEN_EXPIRE) || ''
  }
}

export function clearTokenExpireMeta() {
  try {
    localStorage.removeItem(KEY_ACCESS_TOKEN_EXPIRE)
    localStorage.removeItem(KEY_REFRESH_TOKEN_EXPIRE)
  } catch (e) {
    ignoreStorageError(e)
  }
}

/**
 * @param {{ userId?: string|number, userName?: string, gender?: string|number, avatar?: string }} payload
 */
export function saveLoginUserProfile(payload) {
  if (!payload || typeof payload !== 'object') return
  const { userId, userName, gender, avatar } = payload
  if (userId != null && String(userId).trim() !== '') {
    localStorage.setItem(KEY_USER_ID, String(userId).trim())
  }
  if (userName != null && String(userName).trim() !== '') {
    localStorage.setItem(KEY_USER_NAME, String(userName).trim())
  } else {
    localStorage.removeItem(KEY_USER_NAME)
  }
  /**
   * 仅在有非空性别时写入。refresh 等接口常只带 token、不带 user.gender，
   * 若此处清空 localStorage，会导致「登录有性别、过一会刷新后变 —」。
   */
  if (gender != null && String(gender).trim() !== '') {
    localStorage.setItem(KEY_GENDER, String(gender).trim())
  }
  if (avatar != null) {
    const a = String(avatar).trim()
    if (a !== '') localStorage.setItem(KEY_AVATAR, a)
    else localStorage.removeItem(KEY_AVATAR)
  }
}

/**
 * @returns {{ userId: string, userName: string, gender: string, avatar: string }}
 */
export function getStoredUserProfile() {
  return {
    userId: localStorage.getItem(KEY_USER_ID) || '',
    userName: localStorage.getItem(KEY_USER_NAME) || '',
    gender: localStorage.getItem(KEY_GENDER) || '',
    avatar: localStorage.getItem(KEY_AVATAR) || ''
  }
}

export function clearUserProfile() {
  try {
    localStorage.removeItem(KEY_USER_ID)
    localStorage.removeItem(KEY_USER_NAME)
    localStorage.removeItem(KEY_GENDER)
    localStorage.removeItem(KEY_AVATAR)
    localStorage.removeItem(KEY_AVATAR_PRESET)
    localStorage.removeItem(KEY_PROFILE_PHONE)
    localStorage.removeItem(KEY_PROFILE_EMAIL)
  } catch (e) {
    ignoreStorageError(e)
  }
}

/** 仅更新性别：0 未知 / 1 男 / 2 女；空则按未知写入 0 */
export function saveStoredGender(gender) {
  try {
    const s = gender != null ? String(gender).trim() : ''
    if (s === '0' || s === '1' || s === '2') {
      localStorage.setItem(KEY_GENDER, s)
    } else if (s === '') {
      localStorage.setItem(KEY_GENDER, '0')
    } else {
      localStorage.removeItem(KEY_GENDER)
    }
  } catch (e) {
    ignoreStorageError(e)
  }
}

function readProfileStr(key) {
  try {
    const v = localStorage.getItem(key)
    return v != null ? String(v).trim() : ''
  } catch (e) {
    ignoreStorageError(e)
    return ''
  }
}

function writeProfileStr(key, value) {
  try {
    const s = value != null ? String(value).trim() : ''
    if (s !== '') localStorage.setItem(key, s)
    else localStorage.removeItem(key)
  } catch (e) {
    ignoreStorageError(e)
  }
}

export function getProfilePhone() {
  return readProfileStr(KEY_PROFILE_PHONE)
}

export function saveProfilePhone(value) {
  writeProfileStr(KEY_PROFILE_PHONE, value)
}

export function getProfileEmail() {
  return readProfileStr(KEY_PROFILE_EMAIL)
}

export function saveProfileEmail(value) {
  writeProfileStr(KEY_PROFILE_EMAIL, value)
}

/** @returns {'man'|'woman'|''} */
export function getProfileAvatarPreset() {
  try {
    const v = localStorage.getItem(KEY_AVATAR_PRESET)
    return v === 'man' || v === 'woman' ? v : ''
  } catch (e) {
    ignoreStorageError(e)
    return ''
  }
}

/** @param {'man'|'woman'|''} v */
export function saveProfileAvatarPreset(v) {
  try {
    if (v === 'man' || v === 'woman') {
      localStorage.setItem(KEY_AVATAR_PRESET, v)
    } else {
      localStorage.removeItem(KEY_AVATAR_PRESET)
    }
  } catch (e) {
    ignoreStorageError(e)
  }
}

function pickUserDisplayName(user) {
  if (!user || typeof user !== 'object') return ''
  const keys = ['userName', 'username', 'name', 'nickName', 'realName']
  for (const k of keys) {
    const v = user[k]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

/**
 * 将后端返回的用户资料写入本地，供「我的」、个人资料页读取（性别、账号、手机、邮箱、默认头像等）。
 * 仅当响应体里**出现对应字段**时才更新该项，避免误清空未返回的字段。
 * @param {Record<string, any>} user
 */
export function applyServerUserProfile(user) {
  if (!user || typeof user !== 'object') return
  try {
    if (Object.prototype.hasOwnProperty.call(user, 'userId') || Object.prototype.hasOwnProperty.call(user, 'id')) {
      const uid = user.userId !== undefined ? user.userId : user.id
      if (uid != null && String(uid).trim() !== '') {
        localStorage.setItem(KEY_USER_ID, String(uid).trim())
      }
    }

    if (
      'userName' in user ||
      'username' in user ||
      'name' in user ||
      'nickName' in user ||
      'realName' in user
    ) {
      const n = pickUserDisplayName(user)
      if (n) localStorage.setItem(KEY_USER_NAME, n)
      else localStorage.removeItem(KEY_USER_NAME)
    }

    if (Object.prototype.hasOwnProperty.call(user, 'gender') || Object.prototype.hasOwnProperty.call(user, 'sex')) {
      const gRaw = user.gender !== undefined ? user.gender : user.sex
      if (gRaw != null && String(gRaw).trim() !== '') {
        localStorage.setItem(KEY_GENDER, String(gRaw).trim())
      } else {
        localStorage.removeItem(KEY_GENDER)
      }
    }

    if (
      Object.prototype.hasOwnProperty.call(user, 'phone') ||
      Object.prototype.hasOwnProperty.call(user, 'mobile') ||
      Object.prototype.hasOwnProperty.call(user, 'phoneNumber')
    ) {
      const ph = user.phone !== undefined ? user.phone : user.mobile !== undefined ? user.mobile : user.phoneNumber
      writeProfileStr(KEY_PROFILE_PHONE, ph == null ? '' : ph)
    }

    if (Object.prototype.hasOwnProperty.call(user, 'email')) {
      writeProfileStr(KEY_PROFILE_EMAIL, user.email == null ? '' : user.email)
    }

    if (
      Object.prototype.hasOwnProperty.call(user, 'avatar') ||
      Object.prototype.hasOwnProperty.call(user, 'avatarUrl') ||
      Object.prototype.hasOwnProperty.call(user, 'avatarPath')
    ) {
      const avatar =
        user.avatar !== undefined ? user.avatar : user.avatarUrl !== undefined ? user.avatarUrl : user.avatarPath
      writeProfileStr(KEY_AVATAR, avatar == null ? '' : avatar)
    }

    if (
      Object.prototype.hasOwnProperty.call(user, 'avatarPreset') ||
      Object.prototype.hasOwnProperty.call(user, 'defaultAvatar')
    ) {
      const preset = user.avatarPreset !== undefined ? user.avatarPreset : user.defaultAvatar
      if (preset === 'man' || preset === 'woman') {
        localStorage.setItem(KEY_AVATAR_PRESET, preset)
      } else if (preset === '' || preset == null) {
        localStorage.removeItem(KEY_AVATAR_PRESET)
      }
    }
  } catch (e) {
    ignoreStorageError(e)
  }
}

/** 性别：0 未知 / 1 男 / 2 女 */
export function formatGenderLabel(gender) {
  const g = String(gender).trim()
  if (g === '1') return '男'
  if (g === '2') return '女'
  if (g === '0') return '未知'
  return '—'
}
